import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from "@/hooks/use-toast";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [systemSettings, setSystemSettings] = useState({});
  const { toast } = useToast();

  // Dynamic Backend URL logic
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  // Remove trailing slash if present
  const origin = window.location.origin.replace(/\/$/, '');
  
  // IF on gonzalezroadservices.hu, use IT as backend URL (relative /api will work)
  // IF on localhost, use ENV or localhost:8001
  const backendUrl = (!isLocal) ? origin : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');

  const api = axios.create({
    baseURL: `${backendUrl}/api`,
  });

  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Fetch public settings on mount
  useEffect(() => {
    const fetchPublicSettings = async () => {
        try {
            const res = await axios.get(`${backendUrl}/api/public/settings`);
            setSystemSettings(res.data);
        } catch (error) {
            console.error("Failed to fetch public settings", error);
        }
    };
    fetchPublicSettings();
  }, [backendUrl]);

  const fetchUser = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/users/me');
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      // If 401, token expired
      if (error.response && error.response.status === 401) {
          logout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [token]);

  // Auto-refresh user data when window regains focus or every 60s,
  // so role changes (e.g. admin assigning Promoter) propagate without a re-login.
  useEffect(() => {
    if (!token) return undefined;
    const onFocus = () => fetchUser();
    const onVisibility = () => { if (!document.hidden) fetchUser(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(fetchUser, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = () => {
    // Force redirect to the current origin's backend login endpoint
    // This ensures we stay on gonzalezroadservices.hu if we are there
    window.location.href = `${backendUrl}/api/auth/discord/login`;
  };

  const handleCallback = (jwtToken) => {
    localStorage.setItem('token', jwtToken);
    setToken(jwtToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
    toast({
      title: "Sikeres bejelentkezés",
      description: "Üdvözlünk újra!",
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
    toast({
      title: "Kijelentkezve",
      description: "Sikeresen kijelentkeztél.",
    });
  };

  const updateUser = async (data) => {
    try {
      const response = await api.put('/users/me', data);
      setUser(response.data);
      toast({
        title: "Profil frissítve",
        description: "Az adataid sikeresen mentésre kerültek.",
      });
      return true;
    } catch (error) {
      console.error("Update failed", error);
      toast({
        variant: "destructive",
        title: "Hiba történt",
        description: "Nem sikerült frissíteni az adatokat.",
      });
      return false;
    }
  };

  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const isLandlord = user?.role?.toLowerCase() === 'landlord' || isAdmin;
  const isTenant = !!user;
  const canViewFleet = !!user;
  const hasIcName = !!(user?.ic_name && user.ic_name.trim());
  const isFullyAuthenticated = !!user && hasIcName;
  const isBanned = !!user?.is_banned;

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      handleCallback, 
      updateUser, 
      loading, 
      isAuthenticated: !!user,
      isFullyAuthenticated,
      hasIcName,
      isAdmin,
      isLandlord,
      isTenant,
      canViewFleet,
      isBanned,
      api,
      systemSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
};
